import { api } from "@/shared/api";
import type { components } from "@/lib/api/generated";
import type { Override } from "@/shared/lib/typeOverride";
import type { OrgScopeParams } from "@/shared/hooks/useOrgScopeFilter";

export type CoachingTaskItem = Override<
  components["schemas"]["CoachingTaskDto"],
  {
    scenarioId: number | null;
    scenarioName: string | null;
    assignedCount: number;
    dispatchedCount: number;
    completedCount: number;
    failedCount: number;
    deadline: string | null;
    /** V34/V35:本任务的及格线 / 轮数上限。null = 没单独指定(及格线退默认 60、轮数退场景)。 */
    passScore: number | null;
    maxRounds: number | null;
    // mark: not yet in api-spec.yaml (V24 proposal) — handwritten requires these
    // 0926 第八批 Z3:旧手写契约里可空、真 spec 不带可空信息的字段,在这里补回 | null
    creatorUserId: components["schemas"]["CoachingTaskDto"]["creatorUserId"] | null;
    creatorUserName: components["schemas"]["CoachingTaskDto"]["creatorUserName"] | null;
  }
>;

// local-only: backend schema gap — generated CoachingScenarioItem.dimensions is
// Record<string,never>[]; handwritten typed shape. V24 yaml align via
// 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type ScenarioDimension = {
  dimKey: string;
  dimLabel: string;
  weight: number;
  maxScore?: number | null;
  displayOrder?: number | null;
};

// local-only: backend schema gap — CoachingScenarioItem.aiConfig sub-type absent in generated.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
// OEM H3(2026-10-01):model/evaluatorModel/temperature/maxTokens 已不对客开放(api 从不透传给 ai,
// 模型由 LLM 配置中心统一定);类型保留仅为编辑存量场景时原样回带,界面不再读写。
export type ScenarioAiConfig = {
  model?: string | null;
  evaluatorModel?: string | null;
  temperature?: number | null;
  maxTokens?: number | null;
  // V29 语音陪练:客户音色 id(ai 侧 coaching voices 小表 key)。仅 interactionMode=voice 有意义。
  voice?: string | null;
  // V2 L4:AI 是否知道员工是谁(老客户回访/售后/内部演练才该开)。默认关 —— 陌生客户
  // 上来叫出你的名字是恐怖谷。落 aiConfig 零迁移,与 voice 同一个先例。
  staffAware?: boolean | null;
  // 1024:分身舱「我的音色」(声纹库 id)。非空 = AI 客户用这条克隆声线说话(覆盖 voice),
  // 方言 / 说话风格只对它生效;后端开练时按租户现解析,声纹没了回落默认音色。
  voiceProfileId?: number | null;
  // 1025:语音页客户头像男 / 女。null/缺省 = 自动(预置音色按男女声,我的音色按人设推断)。落 aiConfig 零迁移。
  customerLook?: "male" | "female" | null;
};

// V29 语音陪练:音色小表行(GET /tenant/coaching-voices;previewText 是试听台词)。
export type CoachingVoice = {
  id: string;
  label: string;
  description: string;
  previewText: string;
};

// local-only: backend schema gap — GET /tenant/coaching/dimension-presets response not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type DimensionPreset = {
  presetKey: "sales" | "aftersales" | "consult";
  presetLabel: string;
  dimensions: ScenarioDimension[];
};

// local-only: drift D-14 — generated CoachingScenarioItem misses many fields (knowledgeTags,
// kbWeight, customerPersona, productContext, customerRoleKey, customerToneKey, status, appId,
// aiConfig, emoji) AND types dimensions as Record<string,never>[]. Handwritten covers full DTO.
// V24 reconcile via 2026-05-17-fe-drift-coaching-scenario.md proposal.
export type CoachingScenarioItem = {
  id: number;
  name: string;
  title?: string | null;
  emoji?: string | null;
  difficulty: string | null; // V35: qa_drill 为 null(难度仅 roleplay)
  mode?: string; // "roleplay" | "qa_drill"; 缺省按 roleplay
  interactionMode?: string; // V29 语音陪练:"text" | "voice";缺省按 text(存量场景)
  description: string | null;
  maxRounds: number | null;
  // V31 恢复:后端存的是**逗号分隔字符串**(老表 positions VARCHAR(255)),不是数组。
  // 这里此前写成 string[] 是错的 —— 只因为后端恒返 null 才没暴露;列一恢复,
  // scenarioToForm 里的 .join(",") 立刻就是运行时 TypeError。写请求侧一直就是 string。
  positions?: string | null;
  knowledgeTags: string | null;
  kbWeight: number | null;
  customerPersona: string | null;
  productContext: string | null;
  customerRoleKey?: string | null;
  customerToneKey?: string | null;
  status?: string | null;
  appId?: number | null;
  productCodes?: string[] | null; // 产品作用域:节点 code 数组;空/缺省=通用
  dimensions?: ScenarioDimension[];
  aiConfig?: ScenarioAiConfig | null;
  /**
   * 声音 V2(2026-09-08,B 批已加列):AI 客户的口音(方言 slug,null=普通话)与说话风格。
   * ★当前陪练系统音色还不支持方言 —— 字段先存(即将随克隆客户声线生效),UI 明说。
   * 词表与数字人线同一份(entities/dh-profile voiceV2Options)。
   */
  voiceDialect?: string | null;
  voiceStyle?: string | null;
};

// local-only: backend schema gap — GET /tenant/coaching/evaluations list item DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type CoachingEvaluationItem = {
  id: number;
  employeeName: string;
  dept: string | null;
  taskName: string | null;
  score: number;
  grade: string | null;
  pdfStatus: string;
  pdfUrl: string | null;
  completedAt: string | null;
  // C4-4:AI 离线兜底分(is_low_confidence)标记,与 H5 一致标注,不把安慰分当真实成绩。
  lowConfidence: boolean;
};

// local-only: backend schema gap — GET /tenant/coaching/evaluations/aggregated-by-task DTO
// not in spec. V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
/** 评估考核 Tab 列表层 — 按任务聚合的统计行(对应后端 EvaluationByTaskItem)。 */
export type EvaluationByTaskItem = {
  taskId: number;
  taskName: string;
  scenarioName: string | null;
  status: string;
  deadline: string | null;
  assignedCount: number;
  evaluatedCount: number;
  passCount: number;
  riskCount: number;
  avgScore: number | null;
  topScore: number | null;
  topEmployeeName: string | null;
  bottomScore: number | null;
  bottomEmployeeName: string | null;
  /** 1025:本任务实际及格线(没单独配 = 60),分数配色按它。旧后端不带 → undefined。 */
  passScore?: number;
};

// local-only: backend schema gap — GET /tenant/coaching/stats response DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type CoachingStats = {
  totalEvaluations: number;
  todayEvaluations: number;
  avgScore: number;
  pendingReportCount: number;
  riskEmployeeCount: number;
  scoreDistribution: { range: string; count: number }[];
};

// local-only: backend schema gap — CoachingEvaluationDetail.transcript sub-type not typed in
// generated. V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type TranscriptTurn = {
  role: string;
  turn: number;
  content: string;
  timestamp: string | null;
};

// local-only: backend schema gap — CoachingEvaluationDetail.turnScores sub-type not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type TranscriptTurnScore = {
  turn: number;
  dimension: string;
  score: number;
  comment: string | null;
};

// local-only: drift D-15 — generated CoachingEvaluationDetail misses turnScores/feedback/dept/
// scenarioName/grade fields AND types dimensions/transcript as Record<string,never>. Handwritten
// covers full DTO. V24 reconcile via 2026-05-17-fe-drift-coaching-evaluation.md proposal.
export type CoachingEvaluationDetail = {
  id: number;
  employeeName: string;
  dept: string | null;
  taskName: string | null;
  scenarioName: string | null;
  score: number;
  grade: string | null;
  feedback: string | null;
  dimensions: Record<string, unknown> | null;
  pdfStatus: string;
  pdfUrl: string | null;
  completedAt: string | null;
  transcript: TranscriptTurn[];
  turnScores: TranscriptTurnScore[];
  highlights: string[];
  improvements: string[];
  // C4-4:AI 离线兜底分标记(与列表项/H5 一致)。
  lowConfidence: boolean;
  /** 1025:所属任务实际及格线(自主练习 = 系统默认)。旧后端不带 → undefined。 */
  passScore?: number;
};

// local-only: backend schema gap — POST /tenant/coaching/chat response DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type CoachingChatResponse = {
  sessionId: string;
  reply: string;
  turn: number;
  finished?: boolean;
  score?: number | null;
  feedback?: string | null;
  // V29 语音陪练:客户台词 mp3(语音场景才有;TTS 失败降级 null = 纯文字轮)
  replyAudioUrl?: string | null;
};

// local-only: backend schema gap — POST /tenant/coaching/tasks body DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type TaskCreateRequest = {
  name: string;
  scenarioId: number;
  deadline?: string;
  targetMemberIds: number[];
  /** V34:本次考核的及格线 0-100。不传 = 按系统默认 60。0 是合法值(全员通过),别当成"没填"。 */
  passScore?: number;
  /** V35:本次的对话轮数上限。不传 = 按场景配的走(同一个场景新人练 10 轮、老人练 5 轮)。 */
  maxRounds?: number;
};

// local-only: backend schema gap — PUT /tenant/coaching/tasks/{id} body DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
/**
 * 改任务。字段缺省 = **不改这一项**(后端 PATCH 语义),不是清空。
 *
 * <p>★status 只有 `active` / `closed` 两个值 —— 后端就这两个,此前下拉还列着
 * DISPATCHING / ARCHIVED,是界面比实际乐观。
 */
export type TaskUpdateRequest = {
  name?: string;
  deadline?: string;
  status?: "active" | "closed";
  /** 及格线 0-100。★0 是合法值(这次全员通过),别用 `Number(x) || undefined` 判空——那会把 0 吃掉。 */
  passScore?: number;
  /** 对话轮数上限 1-50;不传 = 不改(留空按场景)。 */
  maxRounds?: number;
};

// local-only: backend schema gap — GET /tenant/coaching/tasks/{id}/members response DTO + status
// enum not in spec. V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type TaskMemberStatus = {
  principalId: number;
  name: string | null;
  department: string | null;
  status: "QUEUED" | "DISPATCHED" | "DELIVERED" | "STARTED" | "COMPLETED" | "FAILED" | "EXPIRED";
  score: number | null;
  dispatchedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  lastFailureReason: string | null;
};

// local-only: backend schema gap — POST /tenant/coaching/tasks/{id}/redispatch body DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type RedispatchRequest = {
  scope?: "undelivered";
  memberIds?: number[];
};

// local-only: backend schema gap — POST /tenant/coaching/tasks/{id}/members body DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type AddMembersRequest = { memberIds: number[] };

// local-only: backend schema gap — GET/PUT /tenant/coaching/settings DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type TenantCoachingSettings = {
  allowSelfInitiated: boolean;
  defaultAiConfig?: Record<string, unknown>;
};

// local-only: backend schema gap — wecom-h5/coaching/my-home sub-type not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type MyHomeTaskItem = {
  taskId: number;
  taskMemberId: number;
  taskName: string;
  scenarioName: string | null;
  deadline: string | null;
  memberStatus: string;
  score: number | null;
};

// local-only: backend schema gap — GET /wecom-h5/coaching/my-home root response DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type MyHomeData = {
  activeTasks: MyHomeTaskItem[];
  completedTasks: MyHomeTaskItem[];
  selfPracticeAllowed: boolean;
};

// local-only: backend schema gap — GET /tenant/coaching/sessions/{sid}/result polling DTO not
// in spec. V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
/** Sprint 5b: 训练评估异步轮询结果。 */
export type SessionResult = {
  status: "evaluating" | "completed" | "failed";
  score: number | null;
  summary: string | null;
  isLowConfidence: number | null;
  error: string | null;
  evaluationId: number | null;
  // V33:做得好的 / 下次改这几点。后端恒返数组(空也是 []),但老镜像可能没有 → 消费方仍要 ?? []。
  highlights: string[] | null;
  improvements: string[] | null;
};

// local-only: backend schema gap — POST /tenant/coaching/scenarios body DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type ScenarioCreateRequest = {
  name: string;
  difficulty?: string; // V35: 仅 roleplay 提交;qa_drill 省略(后端存 null)
  mode?: string; // "roleplay" | "qa_drill"; 省略按 roleplay
  interactionMode?: string; // V29:"text" | "voice";省略按 text(后端白名单 normalize)
  description?: string;
  maxRounds?: number;
  positions?: string;
  customerPersona?: string;
  productContext?: string;
  knowledgeTags?: string;
  kbWeight?: number;
  customerRoleKey?: string | null;
  customerToneKey?: string | null;
  status?: string;
  appId?: number | null;
  productCodes?: string[]; // 产品作用域:节点 code 数组;空数组=通用
  dimensions?: ScenarioDimension[];
  aiConfig?: ScenarioAiConfig;
  /** 声音 V2(B 批已加列):客户口音(方言 slug,null=普通话)/说话风格。roleplay 专属。 */
  voiceDialect?: string | null;
  voiceStyle?: string | null;
};

// local-only: backend schema gap — PUT /tenant/coaching/scenarios/{id} body DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export type ScenarioUpdateRequest = Partial<ScenarioCreateRequest>;

// local-only: backend schema gap — TaskProgressResponse top/bottom score sub-type.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export interface ScoreSummary {
  score: number;
  employeeName: string;
}

// local-only: backend schema gap — legacy /tasks/:id/progress members[] sub-type not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
/** Legacy progress-page member row(/tasks/:id/progress 用,Sprint 4 由 listTaskMembers 替代)。 */
export interface ProgressTaskMemberStatus {
  principalId: number;
  name: string | null;
  department: string | null;
  completed: boolean;
  score: number | null;
  grade: string | null;
  completedAt: string | null;
  evaluationId: number | null;
  pdfStatus: string | null;
  // C4-3:派发状态(QUEUED/DISPATCHED/FAILED/STARTED/COMPLETED),让经理区分「FAILED=未送达需重发」与「已送达待练」。
  dispatchStatus: string | null;
}

// local-only: backend schema gap — GET /tenant/coaching/tasks/{id}/progress root DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export interface TaskProgressResponse {
  taskId: number;
  taskName: string;
  scenarioId: number | null;
  scenarioName: string;
  status: string;
  assignedCount: number;
  completedCount: number;
  pendingCount: number;
  avgScore: number | null;
  topScore: ScoreSummary | null;
  bottomScore: ScoreSummary | null;
  lastLaunchedAt: string | null;
  startDate: string | null;
  deadline: string | null;
  members: ProgressTaskMemberStatus[];
  /** 1025:本任务实际及格线。旧后端不带 → undefined(退 60)。 */
  passScore?: number;
}

// local-only: backend schema gap — GET /tenant/coaching/session/active response DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-coaching.md proposal.
export interface SessionActiveResponse {
  sessionId: string | null;
  turn: number | null;
  expiresAt: number | null;
}

// local-only: backend common PageResponse<T> 泛型未在 spec 参数化(同 aiKefuService PageResponse 注)。V24 yaml align。
export type PageResponse<T> = {
  records: T[];
  total: number;
  page: number;
  size: number;
};

export const coachingService = {
  getStats: () => api.get<CoachingStats>("/tenant/coaching/stats"),
  // #9 组织级归属筛选(后端 resolveOwnerFilter 钳制);scope=all 不下推(等价默认)。
  // F 分页补齐:page(1 基)/size 下推,返 PageResponse;scope 过滤先于分页(后端保序)。
  listTasks: (params?: OrgScopeParams & { page?: number; size?: number }) => {
    const q = new URLSearchParams();
    if (params?.scope && params.scope !== "all") q.set("scope", params.scope);
    if (params?.departmentId != null) q.set("departmentId", String(params.departmentId));
    if (params?.memberId != null) q.set("memberId", String(params.memberId));
    if (params?.page != null) q.set("page", String(params.page));
    if (params?.size != null) q.set("size", String(params.size));
    const qs = q.toString();
    return api.get<PageResponse<CoachingTaskItem>>(`/tenant/coaching/tasks${qs ? `?${qs}` : ""}`);
  },
  listScenarios: () => api.get<CoachingScenarioItem[]>("/tenant/coaching/scenarios"),
  listEvaluations: () => api.get<CoachingEvaluationItem[]>("/tenant/coaching/evaluations"),
  /** 按任务聚合的评估列表(评估考核 Tab 列表层用)。返全量:Hero KPI 全量聚合 + 跨全量排序/搜索,分页在前端做。 */
  listEvaluationsByTask: () =>
    api.get<EvaluationByTaskItem[]>("/tenant/coaching/evaluations/aggregated-by-task"),
  getEvaluationDetail: (id: number) =>
    api.get<CoachingEvaluationDetail>(`/tenant/coaching/evaluations/${id}`),
  generateReport: (id: number) =>
    api.post<void>(`/tenant/coaching/evaluations/${id}/generate-report`, {}),
  createTask: (data: TaskCreateRequest) =>
    api.post<CoachingTaskItem>("/tenant/coaching/tasks", data),
  updateTask: (id: number, data: TaskUpdateRequest) =>
    api.put<CoachingTaskItem>(`/tenant/coaching/tasks/${id}`, data),
  deleteTask: (id: number) => api.delete<void>(`/tenant/coaching/tasks/${id}`),
  createScenario: (data: ScenarioCreateRequest) =>
    api.post<CoachingScenarioItem>("/tenant/coaching/scenarios", data),
  // 「AI 生成整套场景」:据产品线 + 场景目的 + 难度,用产品 KB 起草名称/描述/人设/类型。
  generateScenario: (data: {
    productCodes: string[];
    goal: string;
    difficulty: string;
    knowledgeTags: string;
  }) =>
    api.post<{
      name: string;
      description: string;
      customerPersona: string;
      scenarioType: string;
      difficulty: string;
    }>("/tenant/coaching/scenarios/generate", data),
  // 「AI 生成客户人设」:据产品线 + 客户类型 + 难度,用产品 KB 起草 N 个不同挑战类型的人设(默认 3)。
  generatePersonas: (data: {
    productCodes: string[];
    customerRoleKey: string | null;
    difficulty: string;
    scenarioName: string;
    knowledgeTags: string;
    count: number;
  }) => api.post<{ personas: string[] }>("/tenant/coaching/scenarios/generate-persona", data),
  updateScenario: (id: number, data: ScenarioUpdateRequest) =>
    api.put<CoachingScenarioItem>(`/tenant/coaching/scenarios/${id}`, data),
  deleteScenario: (id: number) => api.delete<void>(`/tenant/coaching/scenarios/${id}`),
  listDimensionPresets: () => api.get<DimensionPreset[]>("/tenant/coaching/dimension-presets"),
  // V29 语音陪练:音色下拉源 + 试听(试听真调 CosyVoice,MANAGER+ 与场景写同门)。
  listVoices: () => api.get<{ voices: CoachingVoice[] }>("/tenant/coaching-voices"),
  // 批次F 外链练习票:企微 webview 不放行 H5 录音 → 系统浏览器外链(短 TTL 单场景作用域)。
  createVoiceLink: (scenarioId: number, taskId?: number | null) =>
    api.post<{
      ticket: string;
      scenarioId: number;
      scenarioName: string;
      expiresInSeconds: number;
    }>("/tenant/coaching/voice-link", { scenarioId, ...(taskId != null ? { taskId } : {}) }),
  // 1024:「我的音色」试听复用数字人线的组合试听(声纹 × 方言 × 风格现合成一小段,返 audioUrl)。
  previewMyVoice: (req: {
    voiceProfileId: number;
    text: string;
    voiceDialect: string | null;
    voiceStyle: string;
  }) =>
    api.post<{ audioUrl: string }>("/digital-human/voices/preview", {
      ...req,
      presetVoice: null,
      voiceLanguage: "zh",
    }),
  previewVoice: (voice: string, text?: string) =>
    api.post<{ voice: string; mime: string; audioBase64: string }>(
      "/tenant/coaching-voices/preview",
      { voice, text: text ?? "" }
    ),
  // taskId 非空=任务制练习:后端据此把会话绑到派发明细(markStarted),练完 completeBySession 回写
  // COMPLETED,任务才闭环消失。空=自主练习(payload 不带 taskId,后端 req.taskId()=null)。
  startChat: (scenarioId: number, taskId?: number | null) =>
    api.post<CoachingChatResponse>("/tenant/coaching/chat/start", {
      scenarioId,
      ...(taskId != null ? { taskId } : {}),
    }),
  chat: (sessionId: string, message: string, scenarioId: number) =>
    api.post<CoachingChatResponse>("/tenant/coaching/chat", { sessionId, message, scenarioId }),
  redispatch: (taskId: number, req: RedispatchRequest) =>
    api.post<number>(`/tenant/coaching/tasks/${taskId}/redispatch`, req),
  addMembers: (taskId: number, req: AddMembersRequest) =>
    api.post<number>(`/tenant/coaching/tasks/${taskId}/members`, req),
  remindIncomplete: (taskId: number) =>
    api.post<number>(`/tenant/coaching/tasks/${taskId}/remind`, {}),
  closeTask: (taskId: number) => api.put<void>(`/tenant/coaching/tasks/${taskId}/close`, {}),
  listTaskMembers: (taskId: number) =>
    api.get<TaskMemberStatus[]>(`/tenant/coaching/tasks/${taskId}/members`),
  getCoachingSettings: () => api.get<TenantCoachingSettings>("/tenant/coaching/settings"),
  updateCoachingSettings: (data: TenantCoachingSettings) =>
    api.put<TenantCoachingSettings>("/tenant/coaching/settings", data),
  getMyHome: () => api.get<MyHomeData>("/wecom-h5/coaching/my-home"),
  /** Sprint 5b: 轮询评估结果(训练 finished 后调用直到 status != 'evaluating')。 */
  getSessionResult: (sessionId: string) =>
    api.get<SessionResult>(`/tenant/coaching/sessions/${sessionId}/result`),
  getSessionResultH5: (sessionId: string) =>
    api.get<SessionResult>(`/wecom-h5/coaching/sessions/${sessionId}/result`),
  getTaskProgress: (taskId: number) =>
    api.get<TaskProgressResponse>(`/tenant/coaching/tasks/${taskId}/progress`),
  getActiveSession: (taskId: number) =>
    api.get<SessionActiveResponse>(`/tenant/coaching/session/active?taskId=${taskId}`),
  downloadReport: async (id: number) => {
    const res = await api.getBlob(`/tenant/coaching/evaluations/${id}/download-report`);
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const match = disposition.match(/filename\*=UTF-8''(.+)/);
    const fileName = match ? decodeURIComponent(match[1]) : `评估报告_${id}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },
};
